import React, { useState, useEffect, useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import notificationService from 'services/notificationService';
import { dineOpsService } from 'api/services/dineOpsService';
import type { DineBoard, DineSpecial } from 'api/types';

/**
 * "Change this run" — one dish's dates, line and position on the board. The
 * dish itself is read-only here on purpose (BR-043): the board never edits
 * the card.
 */
interface Props {
  isOpen: boolean;
  run: DineSpecial;
  onSaved: (board: DineBoard) => void;
  onClose: () => void;
}

const DineOpsSpecialRunModal: React.FC<Props> = ({ isOpen, run, onSaved, onClose }) => {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [note, setNote] = useState('');
  const [order, setOrder] = useState('0');
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setFrom(run.fromDate.slice(0, 10)); setTo(run.toDate.slice(0, 10));
    setNote(run.note ?? ''); setOrder(String(run.displayOrder)); setIsActive(run.isActive); setError(null);
  }, [isOpen, run]);

  const save = useCallback(async () => {
    if (!from || !to) { setError('Both dates are needed.'); return; }
    if (to < from) { setError('The last day cannot be before the first.'); return; }
    setSaving(true); setError(null);
    try {
      const board = await dineOpsService.changeBoardRun(run.id, {
        fromDate: from, toDate: to, note: note.trim() || undefined, displayOrder: Number(order) || 0, isActive, rowVersion: run.rowVersion,
      });
      notificationService.success('Saved', `'${run.itemName}' updated on the board.`);
      onSaved(board);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'The run could not be saved.');
    } finally {
      setSaving(false);
    }
  }, [run, from, to, note, order, isActive, onSaved]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container" style={{ minHeight: 'auto' }}>
        <div className="modal-header">
          <h2 className="modal-title">Change this run</h2>
          <button className="modal-close" onClick={onClose} disabled={saving}><IconSystem.x size={18} /></button>
        </div>
        <div className="modal-content">
          {error && <div className="form-error-message" style={{ marginBottom: 12 }}>{error}</div>}
          <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 10 }}>
            The dish keeps its code, its group, its price and its attributes. Being on the board is a run of
            dates, nothing more, so what it sells still reports under {run.groupName}.
          </div>

          <div className="form-group">
            <label className="form-label">Dish</label>
            <input className="form-input" value={run.itemName} disabled />
            <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
              {run.itemCode} · {run.courseName} › {run.groupName} · {run.isOpenPrice ? 'open priced' : run.price.toFixed(2)}{run.isSoldOut ? ' · currently sold out' : ''}
            </div>
          </div>

          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">On the board from</label>
              <input type="date" className="form-input" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Until</label>
              <input type="date" className="form-input" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Something to say about it</label>
            <input type="text" className="form-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Chef's pick" maxLength={200} />
          </div>

          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">Position on the board</label>
              <input type="number" className="form-input" value={order} min={0} onChange={(e) => setOrder(e.target.value)} />
            </div>
            <div className="form-group" style={{ alignSelf: 'end' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
                <span>Active</span>
              </label>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={() => void save()} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </>
  );
};

export default DineOpsSpecialRunModal;
